import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { isNone } from '@ember/utils';
import EnvironmentService from 'cio/services/environment';
import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';
import ImportModel, { ImportState, UPDATE_TYPES } from 'cio/models/import';
import { importStateGte } from 'cio/helpers/import-state-gte';
import { ImportAttribute, filterEventSamples, SampleType } from 'cio/utils/imports/import-attribute';
import { RESTRICTED_ATTRIBUTES } from 'cio/utils/attributes';
import RouterService from '@ember/routing/router-service';

export default class PreviewController extends Controller {
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  declare model: ImportModel;

  attributes: Record<string, any>[] = [];
  allRestrictedAttributes = RESTRICTED_ATTRIBUTES;

  @computed('model.hasDirtyAttributes')
  get isDirty() {
    return this.model.hasDirtyAttributes;
  }

  @computed('model.preprocessMeta.samples')
  get eventPreview() {
    let { samples } = this.model.preprocessMeta;
    return samples;
  }

  @computed('model.preprocessMeta.samples')
  get timestampColumnWarning() {
    let { samples, attributes } = this.model.preprocessMeta;

    if (samples.length) {
      let [sample] = samples as unknown as SampleType[];

      if (!attributes.includes('_cio_timestamp')) {
        let hasTimestampWarning = Object.keys(JSON.parse(sample.data)).find((element: string) =>
          element.toLowerCase().includes('timestamp'),
        );

        if (hasTimestampWarning) {
          this.analytics.track('import', 'event_column_warning', {
            model: this.model,
            column: 'timestamp',
          });

          return hasTimestampWarning;
        }
      }
    }

    return;
  }

  @action
  setupAttributes() {
    if (isNone(this.model)) {
      throw new Error('Cannot set up attributes because the model is not set');
    }

    this.set('attributes', this.preprocessedAttributes());
  }

  @action
  preprocessedAttributes() {
    let { validateMeta, validateRules, preprocessMeta } = this.model as ImportModel;
    let { mapAttributes, skipAttributes, noId } = validateRules;
    let { attributes, samples } = preprocessMeta;

    let { columnWarnings } = validateMeta ?? {};

    return attributes.map((attribute, index) => {
      let to = mapAttributes[index];
      let skip = skipAttributes[index] ?? false;

      return ImportAttribute.create<any, Record<string, any>>({
        to,
        skip,
        primaryAttribute: (this.model as ImportModel).identifier,
        key: attribute,
        samples: filterEventSamples(samples as unknown as SampleType[], attribute),
        noId,
        initialWarnings: columnWarnings ? columnWarnings[attribute] || [] : [],
        restricted: RESTRICTED_ATTRIBUTES.includes(to ?? ''),
        preprocessedIndex: index,
        sentAttributes: this.environment.current.sentAttributes,
      });
    });
  }

  @action
  reset() {
    this.model.rollbackAttributes();
    this.setupAttributes();
  }

  startValidating = dropTask(async () => {
    try {
      if (!importStateGte([this.model.state, ImportState.Validating])) {
        this.model.set('updateType', UPDATE_TYPES.validate);
        await this.model.save();
      }

      this.router.transitionTo('imports.import.setup.validating', this.model.id);
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  saveAndStartValidating = dropTask(async () => {
    await this.startValidating.perform();
  });
}
